import { useState } from "react";
import MarkdownComponents from "../../utils/MarkdownComponents";

// Markdown explanation
const markdown = `
# Asset Management

Use this page to **create a new asset** or **view the list of assets**.

## Create Asset

\`\`\`
+-------------------------------+
|        New Asset Form         |
+-------------------------------+
| Name:              [_____]    |
| Organisation ID:   [_____]    |
| Contact ID:        [_____]    |
| Group ID:          [_____]    |
| Agent ID:          [_____]    |
| Asset Type ID:     [_____]    |
| Asset Tag:         [_____]    |
| Status ID:         [_____]    |
| Is Critical:       [x]        |
| Effective Date:    [_____]    |
| Purchase Date:     [_____]    |
| Warranty Date:     [_____]    |
| Supply Name:       [_____]    |
| Description:       [_____]    |
| Alert:             [_____]    |
| Azure Entra ID:    [_____]    |
| User Principal:    [_____]    |
| Is Active:         [x]        |
|      [Create Asset]           |
+-------------------------------+
\`\`\`

## Get Assets
\`\`\`
curl -X 'GET' 
  'https://simplisysapi.vizionsys.com/gateway/Asset?page=0&count=50&isActive=true' 
  -H 'accept: */*' 
  -H 'Authorization: Bearer token' 
  -H 'Tenant-Code: vizionsys'
\`\`\`
You can also **fetch and view the list of assets**.
`;
const API_Get= {
  "curl -X": 'GET', 
  "accept": "*/*",
  "Content-Type": "application/json",
  "Authorization": "Bearer token",
  "Tenant-Code": "vizionsys",
};
const API_URL = "https://simplisysapi.vizionsys.com/gateway/Asset";
const API_HEADERS = {
  "curl -X": 'POST', 
  "accept": "*/*",
  "Content-Type": "application/json",
  "Authorization": "Bearer token",
  "Tenant-Code": "vizionsys",
};

const ANIMATION_STEPS = [
  { label: "Sending asset creation request to API...", key: "request" },
  { label: "Received new asset data from server!", key: "response" },
  { label: "Asset creation complete! 🎉", key: "done" },
];
const ANIMATION_List_STEPS = [
  { label: `Get API...${JSON.stringify(API_Get)}`, key: "request" },
  { label: "Received new asset data from server!", key: "response" },
  { label: "Assest recived! 🎉", key: "done" },
];
const defaultForm = {
  id: 0,
  name: "Asset name",
  contactorganisationId: 21823,
  contactId: 7,
  locationId: null,
  groupId: 41,
  agentId: 157,
  assetTypeId: 2,
  assetTag: "Asset Tag",
  statusId: 20,
  isCriticalAsset: true,
  effectiveDate: "2025-07-04T00:00:00",
  purchasePrice: null,
  purchaseDate: "2025-07-03T00:00:00",
  warrantyDate: "2025-07-06T00:00:00",
  supplyName: "siplnam",
  supplyReference: null,
  description: "dev",
  alert: "alrt",
  additionalFields: '{"values":[]}',
  operatingSystem: null,
  operatingSystemVersion: null,
  isActive: true,
  isAccountEnabled: null,
  isComplaint: null,
  azureEntraId: "Obj Id",
  joinType: null,
  activity: "2025-07-03T00:00:00",
  mdm: null,
  ssm: null,
  assetGroup: null,
  userPrincipleName: "use prname",
  assetAttachmentsList: [],
};

export default function AssetPage() {
  const [form, setForm] = useState(defaultForm);
  const [apiData, setApiData] = useState(null);
  const [animationStep, setAnimationStep] = useState(-1);
  const [copyMsg, setCopyMsg] = useState("");
  const [assets, setAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(false);

  // Simulate backend asset creation
  const fakeCreateAsset = (payload) =>
    new Promise((resolve) =>
      setTimeout(
        () =>
          resolve({
            id: 233,
            ...payload,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          }),
        1000
      )
    );

  // Simulate backend get assets
  const fakeGetAssets = () =>
    new Promise((resolve) =>
      setTimeout(
        () =>
          resolve({
            totalCount: 1,
            assets: [
              {
                id: 1,
                name: "Desktop",
                contactorganisationId: 1,
                contactId: 11,
                locationId: 2,
                groupId: 1,
                agentId: 1,
                purchasePrice: "2345",
                assetTypeId: 4,
                assetTag: "LaptopMS 001",
                statusId: 1,
                isCriticalAsset: true,
                effectiveDate: "2024-12-16T00:00:00",
                purchaseDate: "2024-12-14T00:00:00",
                warrantyDate: "2024-12-15T00:00:00",
                supplyName: "HP",
                description: "Additional Fields v Additional Fields",
                alert: "Asset Type",
                azureEntraId: "asset new changes ",
                operatingSystem: "Symphony 22H2",
                operatingSystemVersion: "22H2",
                isActive: true,
                userPrincipleName: "kushal123@gmail.comkushal123@gmail.com",
              },
            ],
          }),
        1000
      )
    );

  const animate = (res) => {
    setAnimationStep(0);
    setApiData({
      url: API_URL,
      headers: API_HEADERS,
      payload: form,
      response: res,
    });
    setTimeout(() => {
      setAnimationStep(1);
      setTimeout(() => {
        setAnimationStep(2);
      }, 900);
    }, 900);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAnimationStep(-1);
    setApiData(null);
    const res = await fakeCreateAsset(form);
    animate(res);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setForm((prev) => ({ ...prev, [name]: checked }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  // cURL command generator with all headers
  const curl = apiData
    ? `curl -X POST ${apiData.url} \\
${Object.entries(apiData.headers)
  .map(([k, v]) => `  -H "${k}: ${v}" \\`)
  .join("\n")}
  -d '${JSON.stringify(apiData.payload, null, 2)}'`
    : "";

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopyMsg("Copied!");
    setTimeout(() => setCopyMsg(""), 1200);
  };

  // GET assets
  const getAssets = async () => {
    setAssetsLoading(true);
    // Simulate API: Use fetch for real API
    const res = await fakeGetAssets();
    setAssets(res.assets);
    setAssetsLoading(false);
  };

  return (
    <>
      <MarkdownComponents markdown={markdown} />

      <form
        onSubmit={handleSubmit}
        style={{
          marginTop: 24,
          background: "#f7f9fa",
          padding: 24,
          borderRadius: 8,
          boxShadow: "0 2px 8px #0001",
        }}
      >
        <h3 style={{ margin: "0 0 16px 0" }}>Create Asset - API Request Preview</h3>
        <div>
          <strong>URL:</strong>
          <pre style={{ background: "#f4f4f4", padding: 8, marginBottom: 6 }}>
            {API_URL}
          </pre>
        </div>
        <div>
          <strong>Headers:</strong>
          <pre style={{ background: "#f4f4f4", padding: 8, marginBottom: 6 }}>
            {JSON.stringify(API_HEADERS, null, 2)}
          </pre>
        </div>
        <div>
          <strong>Payload:</strong>
          <pre style={{ background: "#f4f4f4", padding: 8, marginBottom: 6 }}>
            {JSON.stringify(form, null, 2)}
          </pre>
        </div>
        <div>
          <strong>cURL Command:</strong>
          <pre style={{ background: "#f4f4f4", padding: 8, marginBottom: 6 }}>
            {curl}
          </pre>
          <button
            type="button"
            onClick={() => handleCopy(curl)}
            style={{
              background: "#007bff",
              color: "#fff",
              border: "none",
              borderRadius: 4,
              padding: "4px 10px",
              cursor: "pointer",
              fontSize: 13,
              marginBottom: 8,
            }}
          >
            Copy cURL
          </button>
          <span style={{ color: "#007bff", marginLeft: 8 }}>{copyMsg}</span>
        </div>

        {/* Animation Section */}
        {animationStep >= 0 && apiData && (
          <div
            style={{
              marginTop: 32,
              background: "#fff",
              padding: 24,
              borderRadius: 8,
              boxShadow: "0 2px 8px #0001",
            }}
          >
            <h3>Asset Creation Progress</h3>
            <ul style={{ listStyle: "none", padding: 0 }}>
              {ANIMATION_STEPS.map((step, idx) => (
                <li
                  key={step.key}
                  style={{
                    opacity: animationStep >= idx ? 1 : 0.3,
                    fontWeight: animationStep === idx ? "bold" : "normal",
                    transition: "opacity 0.4s",
                    marginBottom: 10,
                  }}
                >
                  {step.label}
                  {step.key === "response" && animationStep >= idx && (
                    <div style={{ fontSize: 13, color: "#444", marginTop: 6 }}>
                      <strong>Response:</strong>
                      <pre style={{ background: "#f4f4f4", padding: 8 }}>
                        {JSON.stringify(apiData.response, null, 2)}
                      </pre>
                    </div>
                  )}
                  {step.key === "done" && animationStep >= idx && (
                    <div style={{ marginTop: 8, color: "#28a745" }}>
                      <strong>🎉 Asset Created Successfully!</strong>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Minimal form fields */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
          <div style={{ flex: 1, minWidth: 180 }}>
            <label>
              Name:
              <input
                name="name"
                required
                value={form.name}
                onChange={handleChange}
                style={{ width: "100%", marginTop: 4, padding: 8 }}
              />
            </label>
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <label>
              Organisation ID:
              <input
                name="contactorganisationId"
                type="number"
                required
                value={form.contactorganisationId}
                onChange={handleChange}
                style={{ width: "100%", marginTop: 4, padding: 8 }}
              />
            </label>
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <label>
              Contact ID:
              <input
                name="contactId"
                type="number"
                required
                value={form.contactId}
                onChange={handleChange}
                style={{ width: "100%", marginTop: 4, padding: 8 }}
              />
            </label>
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <label>
              Group ID:
              <input
                name="groupId"
                type="number"
                required
                value={form.groupId}
                onChange={handleChange}
                style={{ width: "100%", marginTop: 4, padding: 8 }}
              />
            </label>
          </div>
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 8 }}>
          <div style={{ flex: 1, minWidth: 180 }}>
            <label>
              Agent ID:
              <input
                name="agentId"
                type="number"
                required
                value={form.agentId}
                onChange={handleChange}
                style={{ width: "100%", marginTop: 4, padding: 8 }}
              />
            </label>
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <label>
              Asset Type ID:
              <input
                name="assetTypeId"
                type="number"
                required
                value={form.assetTypeId}
                onChange={handleChange}
                style={{ width: "100%", marginTop: 4, padding: 8 }}
              />
            </label>
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <label>
              Asset Tag:
              <input
                name="assetTag"
                required
                value={form.assetTag}
                onChange={handleChange}
                style={{ width: "100%", marginTop: 4, padding: 8 }}
              />
            </label>
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <label>
              Status ID:
              <input
                name="statusId"
                type="number"
                required
                value={form.statusId}
                onChange={handleChange}
                style={{ width: "100%", marginTop: 4, padding: 8 }}
              />
            </label>
          </div>
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 8 }}>
          <div style={{ flex: 1, minWidth: 180 }}>
            <label>
              Is Critical:
              <input
                type="checkbox"
                name="isCriticalAsset"
                checked={form.isCriticalAsset}
                onChange={handleChange}
                style={{ marginLeft: 8 }}
              />
            </label>
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <label>
              Effective Date:
              <input
                name="effectiveDate"
                type="date"
                required
                value={form.effectiveDate.slice(0, 10)}
                onChange={e =>
                  handleChange({
                    target: {
                      name: "effectiveDate",
                      value: e.target.value + "T00:00:00",
                    },
                  })
                }
                style={{ width: "100%", marginTop: 4, padding: 8 }}
              />
            </label>
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <label>
              Purchase Date:
              <input
                name="purchaseDate"
                type="date"
                required
                value={form.purchaseDate.slice(0, 10)}
                onChange={e =>
                  handleChange({
                    target: {
                      name: "purchaseDate",
                      value: e.target.value + "T00:00:00",
                    },
                  })
                }
                style={{ width: "100%", marginTop: 4, padding: 8 }}
              />
            </label>
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <label>
              Warranty Date:
              <input
                name="warrantyDate"
                type="date"
                required
                value={form.warrantyDate.slice(0, 10)}
                onChange={e =>
                  handleChange({
                    target: {
                      name: "warrantyDate",
                      value: e.target.value + "T00:00:00",
                    },
                  })
                }
                style={{ width: "100%", marginTop: 4, padding: 8 }}
              />
            </label>
          </div>
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 8 }}>
          <div style={{ flex: 1, minWidth: 180 }}>
            <label>
              Supply Name:
              <input
                name="supplyName"
                value={form.supplyName}
                onChange={handleChange}
                style={{ width: "100%", marginTop: 4, padding: 8 }}
              />
            </label>
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <label>
              Description:
              <input
                name="description"
                value={form.description}
                onChange={handleChange}
                style={{ width: "100%", marginTop: 4, padding: 8 }}
              />
            </label>
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <label>
              Alert:
              <input
                name="alert"
                value={form.alert}
                onChange={handleChange}
                style={{ width: "100%", marginTop: 4, padding: 8 }}
              />
            </label>
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <label>
              Azure Entra ID:
              <input
                name="azureEntraId"
                value={form.azureEntraId}
                onChange={handleChange}
                style={{ width: "100%", marginTop: 4, padding: 8 }}
              />
            </label>
          </div>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 8 }}>
          <div style={{ flex: 1, minWidth: 180 }}>
            <label>
              User Principal Name:
              <input
                name="userPrincipleName"
                value={form.userPrincipleName}
                onChange={handleChange}
                style={{ width: "100%", marginTop: 4, padding: 8 }}
              />
            </label>
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <label>
              Is Active:
              <input
                type="checkbox"
                name="isActive"
                checked={form.isActive}
                onChange={handleChange}
                style={{ marginLeft: 8 }}
              />
            </label>
          </div>
        </div>
        <button
          type="submit"
          style={{
            marginTop: 16,
            width: "100%",
            padding: 10,
            background: "#007bff",
            color: "#fff",
            border: "none",
            borderRadius: 4,
            fontSize: 16,
            cursor: "pointer",
          }}
        >
          Create Asset
        </button>
      </form>

      {/* GET assets section */}
      <div
        style={{
          marginTop: 40,
          background: "#f7f9fa",
          padding: 24,
          borderRadius: 8,
          boxShadow: "0 2px 8px #0001",
        }}
      >
        <h3>Get Assets</h3>
        <button
          type="button"
          onClick={getAssets}
          style={{
            marginBottom: 16,
            padding: "8px 18px",
            background: "#17a2b8",
            color: "#fff",
            border: "none",
            borderRadius: 4,
            fontSize: 16,
            cursor: "pointer",
          }}
        >
          {assetsLoading ? "Loading..." : "Get Assets"}
        </button>

        {assets.length > 0 && (
          <div style={{ overflowX: "auto" }}>
    <div
            style={{
              marginTop: 32,
              background: "#fff",
              padding: 24,
              borderRadius: 8,
              boxShadow: "0 2px 8px #0001",
            }}
          >
            <h3>Asset Listing</h3>
            <ul style={{ listStyle: "none", padding: 0 }}>
              {ANIMATION_List_STEPS.map((step, idx) => (
                <li
                  key={step.key}
                  style={{
                    opacity: animationStep >= idx ? 1 : 0.3,
                    fontWeight: animationStep === idx ? "bold" : "normal",
                    transition: "opacity 0.4s",
                    marginBottom: 10,
                  }}
                >
                  {step.label}
                  {step.key === "response" && animationStep >= idx && (
                    <div style={{ fontSize: 13, color: "#444", marginTop: 6 }}>
                      <strong>Response:</strong>
                      <pre style={{ background: "#f4f4f4", padding: 8 }}>
                        {JSON.stringify(apiData.response, null, 2)}
                      </pre>
                    </div>
                  )}
                  {step.key === "done" && animationStep >= idx && (
                    <div style={{ marginTop: 8, color: "#28a745" }}>
                      <strong>🎉 Asset Created Successfully!</strong>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
            <table style={{ borderCollapse: "collapse", width: "100%" }}>
              <thead>
                <tr style={{ background: "#eee" }}>
                  <th style={thStyle}>ID</th>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Tag</th>
                  <th style={thStyle}>Type ID</th>
                  <th style={thStyle}>Status ID</th>
                  <th style={thStyle}>Critical</th>
                  <th style={thStyle}>Active</th>
                  <th style={thStyle}>Agent ID</th>
                  <th style={thStyle}>Group ID</th>
                  <th style={thStyle}>Description</th>
                </tr>
              </thead>
              <tbody>
                {assets.map((asset) => (
                  <tr key={asset.id}>
                    <td style={tdStyle}>{asset.id}</td>
                    <td style={tdStyle}>{asset.name}</td>
                    <td style={tdStyle}>{asset.assetTag}</td>
                    <td style={tdStyle}>{asset.assetTypeId}</td>
                    <td style={tdStyle}>{asset.statusId}</td>
                    <td style={tdStyle}>{asset.isCriticalAsset ? "Yes" : "No"}</td>
                    <td style={tdStyle}>{asset.isActive ? "Yes" : "No"}</td>
                    <td style={tdStyle}>{asset.agentId}</td>
                    <td style={tdStyle}>{asset.groupId}</td>
                    <td style={tdStyle}>{asset.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {assets.length === 0 && !assetsLoading && (
          <div style={{ marginTop: 12, color: "#888" }}>No assets loaded.</div>
        )}
      </div>
    </>
  );
}

// Table styles
const thStyle = {
  border: "1px solid #ccc",
  padding: "6px 10px",
  textAlign: "left",
  fontWeight: "bold",
  background: "#f7f7f7",
};

const tdStyle = {
  border: "1px solid #ccc",
  padding: "6px 10px",
  textAlign: "left",
};

